import { useState, useEffect, useMemo } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";
import useGetResource from "@/hooks/useGetResources";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useGetActiveSubscription from "@/hooks/useGetActiveSubscription";
import useGetFirmAccounts from "@/hooks/useGetFirmAccounts";
import useGetSubscriptionPlans from "@/hooks/useGetSubscriptionPlans";

// Import sub-components
import PageHeader from "@/components/PageHeader";
import NewsFilterToggle from "./components/NewsFilterToggle";
import AnnouncementCard from "./components/AnnouncementCard";
import AnnouncementModal from "./components/AnnouncementModal";
import ImageViewerModal from "./components/ImageViewerModal";
import SubscriptionDialog from "./components/SubscriptionDialog";
import CelebrationDialog from "./components/CelebrationDialog";

// Check if news is new (created within last 24 hours)
const isNewAnnouncement = (createdAt) => {
  const created = new Date(createdAt);
  const now = new Date();
  const diffHours = (now - created) / (1000 * 60 * 60);
  return diffHours < 24;
};

export default function News() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  // Copy to clipboard function
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could add a toast notification here if needed
    } catch (err) {
      console.error(`Failed to copy ${label}:`, err);
    }
  };

  // Handle checking payment status
  const handleCheckPaymentStatus = async () => {
    setIsCheckingPayment(true);
    try {
      const result = await refetchSubscription();
      const updatedSubscription = result.data;

      // Update current status immediately
      if (updatedSubscription?.status) {
        setCurrentStatus(updatedSubscription.status);
      }

      if (updatedSubscription?.status === "ACTIVE") {
        // Close pending dialog and show celebration
        setSubscriptionDialogOpen(false);
        setShowCelebrationDialog(true);
      }
    } catch (error) {
      console.error("Failed to check payment status:", error);
    } finally {
      setIsCheckingPayment(false);
    }
  };

  const [pageTokens, setPageTokens] = useState([null]);
  const [pages, setPages] = useState([]);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [initialTotalCount, setInitialTotalCount] = useState(null);

  // Import and use the subscription hook
  const {
    subscription: subscriptionData,
    subscriptionError,
    refetchSubscription,
    subscriptionLoading,
  } = useGetActiveSubscription();

  // Import firm accounts for bank transfer details
  const { firmAccounts, firmAccountsLoading } = useGetFirmAccounts();

  // Import subscription plans to get plan details
  const { plans } = useGetSubscriptionPlans(true);

  // Add state for checking payment status
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [showCelebrationDialog, setShowCelebrationDialog] = useState(false);

  // Show subscription dialog based on subscription status
  useEffect(() => {
    // Don't process while still loading
    if (subscriptionLoading) {
      return;
    }

    // Always check subscription status when component mounts or subscription data changes
    if (
      subscriptionError?.response?.status === 404 ||
      subscriptionError?.response?.data?.error_code === "NOT_FOUND"
    ) {
      // No active subscription - show dialog and redirect to subscription form
      setSubscriptionDialogOpen(true);
      setCurrentStatus(null);
      setShowCelebrationDialog(false); // Ensure celebration dialog is closed
    } else if (subscriptionData?.status === "PENDING") {
      // Pending subscription - show dialog with appropriate content
      setSubscriptionDialogOpen(true);
      setCurrentStatus(subscriptionData.status);
      setShowCelebrationDialog(false); // Ensure celebration dialog is closed
    } else if (subscriptionData?.status === "ACTIVE") {
      // Active subscription - close all dialogs
      setSubscriptionDialogOpen(false);
      setShowCelebrationDialog(false);
      setCurrentStatus(subscriptionData.status);
    } else if (subscriptionData?.status) {
      // Any other status - update current status
      setCurrentStatus(subscriptionData.status);
    }
  }, [subscriptionError, subscriptionData, subscriptionLoading]);

  // Force subscription check every time the news page is viewed
  useEffect(() => {
    // This will trigger the subscription check when the component mounts
    // The useGetActiveSubscription hook will automatically fetch the data

    // Force a subscription check when the page loads
    if (refetchSubscription) {
      refetchSubscription();
    }
  }, [refetchSubscription]);

  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [filter, setFilter] = useState("all"); // "all", "new"
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageList, setImageList] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const currentPageToken = pageTokens.at(-1);
  const queryUrl = currentPageToken
    ? `/api/v1/organizations/announcements?cursor=${currentPageToken}`
    : `/api/v1/organizations/announcements`;

  const {
    data: newsPage,
    isLoading,
    error,
  } = useGetResource(["news", currentPageToken], queryUrl);

  useEffect(() => {
    if (!newsPage?.records) return;

    const index = pageTokens.length - 1;

    // For first page (page 0), we should reset pages array and capture initial total count
    if (index === 0) {
      setPages([newsPage.records]);
      setCurrentPageIndex(0);
      // Capture the initial total count only from the first page
      if (initialTotalCount === null && newsPage.total_count) {
        setInitialTotalCount(newsPage.total_count);
      }
    } else {
      // For subsequent pages, add to pages array
      setPages((prev) => {
        const copy = [...prev];
        copy[index] = newsPage.records;
        return copy;
      });
      if (index > currentPageIndex) setCurrentPageIndex(index);
    }
  }, [newsPage, pageTokens, currentPageIndex, initialTotalCount]);

  useEffect(() => {
    if (filter === "new") {
      setCurrentPageIndex(0);
      setPageTokens([null]);
      setPages([]);
      setInitialTotalCount(null); // Reset total count when filter changes
    }
  }, [filter]);

  const aggregatedNews = useMemo(
    () => pages.slice(0, currentPageIndex + 1).flat(),
    [pages, currentPageIndex]
  );

  const filteredNews = useMemo(() => {
    if (filter === "new") {
      return aggregatedNews.filter((a) => isNewAnnouncement(a.created_at));
    }
    return aggregatedNews;
  }, [aggregatedNews, filter]);

  const hasNext = newsPage?.next_page;

  const handleNext = () => {
    if (newsPage?.next_page)
      setPageTokens((tokens) => [...tokens, newsPage.next_page]);
  };

  const openModal = (announcement) => {
    setSelectedAnnouncement(announcement);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedAnnouncement(null);
  };

  const handleImageClick = (announcement, attachment) => {
    const attachments =
      announcement?.attachments.filter((att) => att.is_image) || [];
    const index = attachments.findIndex((att) => att.id === attachment.id);
    setImageList(attachments);
    setActiveImageIndex(index);
    setImageModalOpen(true);
  };

  const newAnnouncementsCount = aggregatedNews.filter((a) =>
    isNewAnnouncement(a.created_at)
  ).length;

  return (
    <>
      {/* Main Content */}
      {isLoading && pages.length === 0 ? (
        <Box textAlign="center" p={4}>
          <CircularProgress aria-label={t("news.loadingAria")} />
        </Box>
      ) : error ? (
        <Box textAlign="center" p={4}>
          <Typography color="error">{t("news.errorText")}</Typography>
        </Box>
      ) : !aggregatedNews.length ? (
        <Box textAlign="center" p={4}>
          <Typography>{t("news.noAnnouncementsText")}</Typography>
        </Box>
      ) : (
        <>
          {/* Compact Enterprise Header */}
          <PageHeader
            icon="📢"
            title={t("news.headerTitle", {
              defaultValue: "Company News & Announcements",
            })}
            subtitle={t("news.headerSubtitle", {
              defaultValue:
                "Stay updated with the latest company announcements and important updates",
            })}
            stats={[
              {
                label: `${aggregatedNews.length}/${
                  initialTotalCount || aggregatedNews.length
                }`,
              },
            ]}
            t={t}
            translationPrefix="news"
            compact={true}
          />

          {/* Filter Toggle */}
          <NewsFilterToggle
            filter={filter}
            onFilterChange={setFilter}
            newAnnouncementsCount={newAnnouncementsCount}
          />

          {/* Grid Layout */}
          <Box sx={{ px: 3 }}>
            <Grid container spacing={3} aria-label={t("news.gridAria")}>
              {filteredNews.map((announcement) => (
                <Grid key={announcement.id} size={{ xs: 12, md: 6 }}>
                  <AnnouncementCard
                    announcement={announcement}
                    onReadMore={() => openModal(announcement)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Pagination */}
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!hasNext}
              size="large"
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                boxShadow: theme.shadows[4],
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                "&:hover": {
                  boxShadow: theme.shadows[8],
                  transform: "translateY(-2px)",
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 100%)`,
                },
                "&.Mui-disabled": {
                  background: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                  boxShadow: "none",
                  transform: "none",
                  cursor: "not-allowed",
                  opacity: 0.6,
                },
                transition: "all 0.3s ease",
              }}
              aria-label={t("news.loadMoreAria")}
            >
              {t("news.loadMoreButton")}
            </Button>
          </Box>

          {/* Announcement Modal */}
          <AnnouncementModal
            open={modalOpen}
            onClose={closeModal}
            announcement={selectedAnnouncement}
            onImageClick={handleImageClick}
          />

          {/* Image Viewer Modal */}
          <ImageViewerModal
            open={imageModalOpen}
            onClose={() => setImageModalOpen(false)}
            imageList={imageList}
            activeImageIndex={activeImageIndex}
            onPrevImage={() => setActiveImageIndex((prev) => prev - 1)}
            onNextImage={() => setActiveImageIndex((prev) => prev + 1)}
          />
        </>
      )}

      {/* Subscription Required/Pending Dialog */}
      <SubscriptionDialog
        open={subscriptionDialogOpen}
        onClose={() => setSubscriptionDialogOpen(false)}
        currentStatus={currentStatus}
        subscriptionData={subscriptionData}
        firmAccounts={firmAccounts}
        firmAccountsLoading={firmAccountsLoading}
        plans={plans}
        isCheckingPayment={isCheckingPayment}
        onCheckPaymentStatus={handleCheckPaymentStatus}
        onNavigateToSubscription={() => {
          setSubscriptionDialogOpen(false);
          navigate("/subscription");
        }}
        copyToClipboard={copyToClipboard}
      />

      {/* Celebration Dialog for Payment Confirmation */}
      <CelebrationDialog
        open={showCelebrationDialog}
        onClose={() => setShowCelebrationDialog(false)}
      />
    </>
  );
}
